import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/AthleteSettings.css';
import AthleteSecurity from '../AthleteSecurity/AthleteSecurity';
import { useTranslation } from '../../i18n';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

import GarminLogo from '../../assets/Garmin.png';
import CorosLogo from '../../assets/COROS.png';
import SuuntoLogo from '../../assets/Suunto.png';
import PolarLogo from '../../assets/Polar.png';
import StravaConnect from './StravaConnect';

const API_URL = API_BASE_URL;

// ─── Garmin Connect ───────────────────────────────────────────────────────────
const GarminConnect = ({ userId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/garmin/status/${userId}`);
      setStatus(res.data);
    } catch (e) {
      setMessage({ type: 'error', text: 'Error obteniendo estado de Garmin' });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleConnect = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setMessage({ type: 'error', text: 'Ingresa tu email y contraseña de Garmin Connect' });
      return;
    }
    const { email, password } = form;
    setForm((current) => ({ ...current, password: '' }));
    try {
      setConnecting(true);
      setMessage(null);
      await axios.post(`${API_URL}/api/garmin/connect`, {
        userId, garminEmail: email, garminPassword: password,
      });
      setMessage({ type: 'success', text: 'Cuenta Garmin conectada. Sincronizando actividades...' });
      setShowForm(false);
      setForm({ email: '', password: '' });
      setTimeout(fetchStatus, 3000);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Error conectando con Garmin' });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar tu cuenta de Garmin Connect?')) return;
    try {
      await axios.post(`${API_URL}/api/garmin/disconnect/${userId}`);
      setMessage({ type: 'success', text: 'Cuenta Garmin desconectada' });
      fetchStatus();
    } catch (e) {
      setMessage({ type: 'error', text: 'Error desconectando Garmin' });
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setMessage(null);
      await axios.post(`${API_URL}/api/garmin/sync/${userId}`, { hours: 168 });
      setMessage({ type: 'success', text: 'Sincronización iniciada. Las actividades aparecerán en unos momentos.' });
      setTimeout(fetchStatus, 5000);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Error iniciando sincronización' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="tz-conn-loading">Cargando...</div>;

  return (
    <div className="tz-connections">
      {message && (
        <div className={`tz-conn-message tz-conn-message--${message.type}`}>{message.text}</div>
      )}

      {/* Garmin */}
      <div className={`tz-device-card ${status?.connected ? 'connected' : ''}`}>
        <div className="tz-device-logo tz-device-logo--light">
          <img src={GarminLogo} alt="Garmin Connect" />
        </div>
        <div className="tz-device-divider" />
        <div className="tz-device-info">
          <div className="tz-device-name">Garmin Connect</div>
          {status?.connected ? (
            <>
              <div className="tz-device-meta">{status.email}</div>
              <div className="tz-device-status">
                <span className="tz-status-dot on" />
                <span className="tz-status-label on">Conectado</span>
              </div>
              <div className="tz-sync-time">
                Última sincronización: {status.lastSync ? new Date(status.lastSync).toLocaleString('es-CL') : 'Nunca'}
              </div>
            </>
          ) : (
            <>
              <div className="tz-device-meta">No conectado</div>
              <div className="tz-device-status">
                <span className="tz-status-dot" />
                <span className="tz-status-label">Desconectado</span>
              </div>
            </>
          )}
        </div>
        <div className="tz-device-actions">
          {status?.connected ? (
            <>
              <button className="tz-btn tz-btn-outline" onClick={handleSync} disabled={syncing}>
                {syncing ? 'Sincronizando...' : 'Sincronizar'}
              </button>
              <button className="tz-btn tz-btn-danger" onClick={handleDisconnect}>
                Desconectar
              </button>
            </>
          ) : (
            <button
              className="tz-btn tz-btn-primary"
              onClick={() => {
                setForm({ email: '', password: '' });
                setShowForm(true);
              }}
            >
              Conectar
            </button>
          )}
        </div>
      </div>

      {/* Formulario de conexión */}
      {showForm && !status?.connected && (
        <form className="tz-conn-form" onSubmit={handleConnect}>
          <h4 className="tz-conn-form-title">Conectar Garmin Connect</h4>
          <div className="tz-conn-form-group">
            <label>Email de Garmin Connect</label>
            <input
              type="email"
              autoComplete="username"
              placeholder="tu@email.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div className="tz-conn-form-group">
            <label>Contraseña de Garmin Connect</label>
            <input
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
          </div>
          <p className="tz-conn-privacy">
            Tus credenciales se usan únicamente para autorizar la conexión con Garmin.
          </p>
          <div className="tz-conn-form-actions">
            <button type="submit" className="tz-btn tz-btn-primary" disabled={connecting}>
              {connecting ? 'Conectando...' : 'Conectar'}
            </button>
            <button
              type="button"
              className="tz-btn tz-btn-outline"
              onClick={() => {
                setForm({ email: '', password: '' });
                setShowForm(false);
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

    </div>
  );
};

// ─── AthleteSettings ──────────────────────────────────────────────────────────
const AthleteSettings = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('conexiones');
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');

  const tabs = [
    { id: 'perfil',      label: t('athleteDashboard.tabProfile'),   icon: '👤' },
    { id: 'zonas',       label: t('athleteDashboard.tabZones'),      icon: '📊' },
    { id: 'imagenes',    label: t('athleteDashboard.tabImages'),     icon: '🖼️' },
    { id: 'seguridad',   label: t('athleteDashboard.tabSecurity'),   icon: '🔒' },
    { id: 'conexiones',  label: 'Conexiones',                        icon: '🔌' },
  ];

  return (
    <div className="tz-settings-page">

      {/* Header */}
      <div className="tz-settings-topbar">
        <button
          className="tz-settings-back"
          onClick={() => { navigate('/athlete-dashboard'); window.location.href = '/athlete-dashboard'; }}
        >
          ← {t('athleteDashboard.backToDashboard')}
        </button>
        <h1 className="tz-settings-title">{t('athleteDashboard.settingsTitle')}</h1>
      </div>

      <div className="tz-settings-layout">

        {/* Sidebar */}
        <nav className="tz-settings-sidebar">
          <div className="tz-sidebar-header">Opciones</div>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tz-sidebar-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tz-sidebar-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Contenido */}
        <div className="tz-settings-main">
          <div className="tz-settings-main-header">
            <div className="tz-settings-main-title">
              {tabs.find(t => t.id === activeTab)?.label}
            </div>
          </div>
          <div className="tz-settings-main-body">

            {activeTab === 'perfil' && (
              <div className="tz-settings-section">
                <p>Edita tu información personal, datos de entrenamiento y preferencias.</p>
                <button className="tz-btn tz-btn-primary" onClick={() => navigate('/athlete-profile')}>
                  {t('athleteDashboard.goToProfile')}
                </button>
              </div>
            )}

            {activeTab === 'zonas' && (
              <div className="tz-settings-section">
                <p>Configura tus zonas de frecuencia cardíaca y potencia.</p>
                <button className="tz-btn tz-btn-primary" onClick={() => navigate('/training-zones')}>
                  {t('athleteDashboard.configureZones')}
                </button>
              </div>
            )}

            {activeTab === 'imagenes' && (
              <div className="tz-settings-section">
                <p>Actualiza tu foto de perfil y foto de portada.</p>
                <button className="tz-btn tz-btn-primary" onClick={() => navigate('/change-image')}>
                  {t('athleteDashboard.changeImage')}
                </button>
              </div>
            )}

            {activeTab === 'seguridad' && (
              <div className="tz-settings-section">
                <AthleteSecurity />
              </div>
            )}

            {activeTab === 'conexiones' && (
              <div className="tz-connections">
                {/* Garmin */}
                <GarminConnect userId={userId} />

                {/* Strava */}
                <StravaConnect userId={userId} />

                {/* COROS - próximamente */}
                <div className="tz-device-card">
                  <div className="tz-device-logo tz-device-logo--dark">
                    <img src={CorosLogo} alt="COROS" />
                  </div>
                  <div className="tz-device-divider" />
                  <div className="tz-device-info">
                    <div className="tz-device-name">COROS Training Hub</div>
                    <div className="tz-device-meta">No conectado</div>
                    <div className="tz-device-status">
                      <span className="tz-status-dot" />
                      <span className="tz-status-label">Desconectado</span>
                    </div>
                  </div>
                  <div className="tz-device-actions">
                    <span className="tz-badge-soon">Próximamente</span>
                  </div>
                </div>

                {/* Suunto - próximamente */}
                <div className="tz-device-card">
                  <div className="tz-device-logo tz-device-logo--dark">
                    <img src={SuuntoLogo} alt="Suunto" />
                  </div>
                  <div className="tz-device-divider" />
                  <div className="tz-device-info">
                    <div className="tz-device-name">Suunto App</div>
                    <div className="tz-device-meta">No conectado</div>
                    <div className="tz-device-status">
                      <span className="tz-status-dot" />
                      <span className="tz-status-label">Desconectado</span>
                    </div>
                  </div>
                  <div className="tz-device-actions">
                    <span className="tz-badge-soon">Próximamente</span>
                  </div>
                </div>

                {/* Polar - próximamente */}
                <div className="tz-device-card">
                  <div className="tz-device-logo tz-device-logo--dark">
                    <img src={PolarLogo} alt="Polar" />
                  </div>
                  <div className="tz-device-divider" />
                  <div className="tz-device-info">
                    <div className="tz-device-name">Polar Flow</div>
                    <div className="tz-device-meta">No conectado</div>
                    <div className="tz-device-status">
                      <span className="tz-status-dot" />
                      <span className="tz-status-label">Desconectado</span>
                    </div>
                  </div>
                  <div className="tz-device-actions">
                    <span className="tz-badge-soon">Próximamente</span>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AthleteSettings;
